import { useEffect, useState, type ReactNode } from 'react';
import { useZeitwahl } from '../provider';

export interface NoticeProps {
  kind?: 'error' | 'warning' | 'info' | 'success';
  children: ReactNode;
}

/** Errors are announced assertively (`alert`); everything else politely (`status`). */
export function Notice({ kind = 'info', children }: NoticeProps): ReactNode {
  return (
    <div className={`zw-notice zw-notice--${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export interface RetryButtonProps {
  /** Seconds to wait before the button becomes active (`retryAfterSeconds`). */
  seconds?: number;
  onRetry: () => void;
}

export function RetryButton({ seconds = 0, onRetry }: RetryButtonProps): ReactNode {
  const { t } = useZeitwahl();
  const [left, setLeft] = useState(Math.max(0, Math.ceil(seconds)));

  useEffect(() => {
    if (left <= 0) return;
    const id = setTimeout(() => setLeft(left - 1), 1000);
    return () => clearTimeout(id);
  }, [left]);

  return (
    <button
      type="button"
      className="zw-button zw-button--secondary"
      disabled={left > 0}
      onClick={onRetry}
    >
      {left > 0 ? t('retryIn', { seconds: left }) : t('retry')}
    </button>
  );
}

/** Back-chevron used by "Back" / "Change time" controls. */
export function Chevron({ direction }: { direction: 'left' | 'right' }): ReactNode {
  return (
    <svg
      className="zw-icon"
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d={direction === 'left' ? 'M10 3 5 8l5 5' : 'M6 3l5 5-5 5'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
